import { BaseStore } from "@/Common/Stores/BaseStore";

/**
 * The optional settings for an overlay.
 */
type OverlayOptions = {
    /**
     * Whether the overlay should be transparent, with no color overlay.
     */
    transparent?: boolean,

    /**
     * The callback to invoke when this overlay is clicked.
     */
    onClicked?: () => void

    /**
     * Sets the overlay z-index to 201.
     */
    zIndexAbovePopup?: boolean;
};

let defaultInstance: OverlayStore;

class OverlayStore extends BaseStore {
    /**
     * The modal overlays currently being shown.
     */
    private overlays: {
        [name: string]: {
            /**
             * Whether the overlay has a colored background.
             */
            hasBackground: boolean;

            /**
             * The callback to invoke when this overlay is clicked.
             */
            onClicked: () => void;

            /**
             * A number incremented for higher overlays.
             */
            order: number

            /**
             * Sets the overlay z-index to 201.
             */
            zIndexAbovePopup?: boolean;
        }
    } = {};

    public static createSingleton(): OverlayStore {
        return this.createStoreSingleton(defaultInstance, () => defaultInstance = new OverlayStore());
    }

    /**
     * Whether any modal overlay is enabled.
     */
    public hasOverlay(): boolean {
        return Object.keys(this.overlays).length > 0;
    }

    /**
     * Whether any modal overlay is enabled and has a background color.
     */
    public overlaysHaveBackground(): boolean {
        return Object
            .keys(this.overlays)
            .some(key => this.overlays[key].hasBackground);
    }

    /**
     * Whether any modal overlay is enabled and has a z-index.
     */
    public overlaysHaveZIndexAbovePopup(): boolean {
        return Object
            .keys(this.overlays)
            .some(key => this.overlays[key]?.zIndexAbovePopup ?? false);
    }

    /**
     * Show a modal overlay.
     * @param key A unique key for the overlay, which can be used to remove it later.
     * @param options The optional overlay settings.
     */
    public showOverlay(key: string, options: OverlayOptions = null): void {
        if (!key)
            throw "Can't add an overlay with no key.";

        options = options || {};
        this.overlays[key] = {
            hasBackground: options.transparent !== true,
            onClicked: options.onClicked,
            zIndexAbovePopup: options.zIndexAbovePopup,
            order: sessionStore.newId()
        };
        this.EmitChangeToListeners();
    }

    /**
     * Hide a modal overlay.
     * @param key The unique key for the overlay used to add it.
     */
    public hideOverlay(key: string): void {
        if (!key)
            throw "Can't hide an overlay with no key.";

        if (this.overlays[key]) {
            delete this.overlays[key];
            this.EmitChangeToListeners();
        }
    }

    /**
     * Hide all shown overlays
     */
    public hideAllOverlays(): void {
        if (!this.hasOverlay()) return;

        this.overlays = {};
        this.EmitChangeToListeners();
    }

    /**
     * Handle the user clicking on an overlay.
     */
    public handleOverlayClick(): void {
        // get top overlay
        const overlay = Object
            .keys(this.overlays)
            .map(key => this.overlays[key])
            .sortByProperty(p => p.order)
            .firstOrDefault();

        // raise callback
        if (overlay && overlay.onClicked)
            overlay.onClicked();
    }
}

export { OverlayOptions, OverlayStore, defaultInstance as overlayStore };
